import React, { useEffect, useState } from 'react';
import {
  Box,
  VStack,
  Text,
  Collapse,
  HStack,
  Icon,
  Link as ChakraLink,
  useBreakpointValue,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Link, useLocation } from 'react-router-dom';
import useOrgClaims from '../../hooks/useOrgClaims';
import { MENU, allowByRole } from '../../menu.config';

const STORAGE_KEY = 'fixihub.sidebar.salesReports.open';

export default function SalesReportsGroup() {
  // Always call hooks in a stable order
  const { claims } = useOrgClaims();
  const role = (claims?.role || '').toLowerCase();
  const location = useLocation();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Local state with desktop/mobile default (unless saved)
  const [isOpen, setIsOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'true' || saved === 'false') return saved === 'true';
    } catch {}
    return !isMobile;
  });

  // Set initial default only if nothing saved yet
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === null) {
        const next = !isMobile;
        setIsOpen(next);
        localStorage.setItem(STORAGE_KEY, String(next));
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // Render by default while claims load; hide only if we know user is not admin/superadmin
  const isKnownNonAdmin = role && role !== 'admin' && role !== 'superadmin';
  if (isKnownNonAdmin) return null;

  const links = allowByRole(MENU.salesReports, role);

  const toggle = () => {
    setIsOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {}
      return next;
    });
  };

  const isActive = (path) =>
    location.pathname === path || (location.pathname.startsWith(path + '/') && path !== '/leads');

  return (
    <Box w="full">
      {/* Group header (text only, no icon) */}
      <Box
        as="button"
        w="full"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={toggle}
        py={2}
        _hover={{ bg: 'whiteAlpha.200' }}
        rounded="md"
      >
        <Text fontWeight="bold" color="white" noOfLines={1}>
          Sales Reports
        </Text>
        {isOpen ? <ChevronDownIcon color="white" /> : <ChevronRightIcon color="white" />}
      </Box>

      <Collapse in={isOpen} animateOpacity style={{ width: '100%' }}>
        <VStack align="start" spacing={1} w="full">
          {links.map((sub) => {
            const active = isActive(sub.href);
            return (
              <ChakraLink
                as={Link}
                to={sub.href}
                key={sub.label}
                fontSize="sm"
                py={2}
                pl={4}
                pr={3}
                w="full"
                rounded="md"
                color={active ? 'teal.200' : 'gray.200'}
                fontWeight={active ? 'semibold' : 'normal'}
                bg={active ? 'whiteAlpha.200' : 'transparent'}
                _hover={{
                  color: 'cyan.300',
                  bg: 'whiteAlpha.100',
                  textDecoration: 'none',
                }}
                _focus={{ outline: 'none', boxShadow: 'none' }}
              >
                <HStack spacing={2} align="center">
                  {sub.icon ? <Icon as={sub.icon} boxSize={4} /> : null}
                  <Text as="span">{sub.label}</Text>
                </HStack>
              </ChakraLink>
            );
          })}
        </VStack>
      </Collapse>
    </Box>
  );
}
